import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { useAuth } from './contexts/AuthContext.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import NavigationBar from './components/NavigationBar.jsx';
import Contact from './pages/Contact.jsx';

Chart.register(...registerables);

// Lazy-loaded pages
const HomePage       = lazy(() => import('./pages/HomePage.js'));
const CompanyDetails = lazy(() => import('./pages/CompanyDetails.js'));
const SignIn         = lazy(() => import('./pages/SignIn.js'));
const SignUp         = lazy(() => import('./pages/SignUp.jsx'));
const CreateSite     = lazy(() => import('./pages/CreateSite.js'));
const SiteDashboard  = lazy(() => import('./pages/SiteDashboard.js'));
const SiteDetails    = lazy(() => import('./pages/SiteDetails.js'));
const FormsTracking  = lazy(() => import('./components/FormsTracking.jsx'));
const EditSite       = lazy(() => import('./pages/EditSite.js'));
const DeleteAccount  = lazy(() => import('./pages/delete-account.jsx'));
const PrivacyPolicy  = lazy(() => import('./pages/PrivacyPolicy.js'));

// Nested SiteDetails components
const SiteInfo                 = lazy(() => import('./components/SiteInfo.jsx'));
const DailySignIns             = lazy(() => import('./components/DailySignIns.jsx'));
const InductionRegister        = lazy(() => import('./components/InductionRegister.jsx'));
const HazardRegister           = lazy(() => import('./components/HazardRegister.jsx'));
const ToolboxTalks             = lazy(() => import('./components/ToolboxTalks.jsx'));
const IncidentReports          = lazy(() => import('./components/IncidentReports.jsx'));
const TaskAnalysis             = lazy(() => import('./components/TaskAnalysis.jsx'));
const UploadedDocs             = lazy(() => import('./components/UploadedDocs.jsx'));
const CompanyRulesAndInduction = lazy(() => import('./components/CompanyRulesAndInduction.jsx'));
const SiteInspection           = lazy(() => import('./components/SiteInspection.jsx'));
const GeofenceConfigPage       = lazy(() => import('./components/GeofenceConfigPage.jsx'));

// Guest-flow components
const GuestSignIn   = lazy(() => import('./components/GuestSignIn.jsx'));
const InductionForm = lazy(() => import('./components/InductionForm.jsx'));
const HazardList    = lazy(() => import('./components/HazardList.jsx').then(mod => ({ default: mod.HazardList })));
const HazardDetail  = lazy(() => import('./components/HazardDetail.jsx').then(mod => ({ default: mod.HazardDetail })));

const Redirect = lazy(() => import('./components/Redirect.jsx'));

function App() {
  const { currentUser } = useAuth();

  return (
    <Router>
      <ErrorBoundary>
        <NavigationBar user={currentUser} />
        <Suspense fallback={<div>Loading…</div>}>
          <Routes>
            {/* Public routes available to everyone */}
            <Route path="/" element={<HomePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/contact" element={<Contact />} />

            {/* Guest routes (no auth required) */}
            <Route path="/redirect" element={<Redirect />} />
            <Route path="/guestsignin/:companyId/:siteId" element={<GuestSignIn />} />
            <Route path="/guestsignin/:companyId/:siteId/induction" element={<InductionForm />} />
            <Route path="/guestsignin/:companyId/:siteId/hazards" element={<HazardList />} />
            <Route path="/guestsignin/:companyId/:siteId/hazards/:hazardId" element={<HazardDetail />} />

            {/* Auth pages only for NOT logged-in users */}
            <Route element={<PublicRoute currentUser={currentUser} />}>
              <Route path="/sign-in" element={<SignIn />} />
              <Route path="/sign-up" element={<SignUp />} />
            </Route>

            {/* Protected routes (requires login) */}
            <Route element={<ProtectedRoute currentUser={currentUser} />}>
              <Route path="/companies/:companyId" element={<CompanyDetails />} />
              <Route path="/create-site" element={<CreateSite />} />
              <Route path="/site-dashboard" element={<SiteDashboard />} />
              <Route path="/sites/:siteId/edit" element={<EditSite />} />
              <Route path="/account/delete" element={<DeleteAccount />} />
              <Route path="/companies/:companyId/sites/:siteId/forms-tracking" element={<FormsTracking />} />

              <Route path="/sites/:siteId" element={<SiteDetails />}>
                <Route index element={<Navigate to="site-info" replace />} />
                <Route path="site-info" element={<SiteInfo />} />
                <Route path="daily-sign-ins" element={<DailySignIns />} />
                <Route path="induction-register" element={<InductionRegister />} />
                <Route path="hazard-register" element={<HazardRegister />} />
                <Route path="toolbox-talks" element={<ToolboxTalks />} />
                <Route path="incident-reports" element={<IncidentReports />} />
                <Route path="task-analysis" element={<TaskAnalysis />} />
                <Route path="uploaded-docs" element={<UploadedDocs />} />
                <Route path="company-rules-and-induction" element={<CompanyRulesAndInduction />} />
                <Route path="inspections" element={<SiteInspection />} />
                <Route path="geofence-config" element={<GeofenceConfigPage />} />
              </Route>
            </Route>

            {/* Catch-all fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </ErrorBoundary>
    </Router>
  );
}

function PublicRoute({ currentUser }) {
  return currentUser ? <Navigate to="/" replace /> : <Outlet />;
}

function ProtectedRoute({ currentUser }) {
  return currentUser ? <Outlet /> : <Navigate to="/sign-in" replace />;
}

export default App;
